import request, { longRunningRequest } from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type BatchOutputMutationStatus = 'Skipped' | 'Completed' | 'Failed' | 'ValidationFailed' | string;

export type BatchOutputApproveResponse = {
    status: BatchOutputMutationStatus;
    message?: string;
    rawImageId?: number;
    barcode?: string;
    barcodeFullyApproved?: boolean;
    readyProcessedPaths?: string[];
    fileTransferTriggered?: boolean;
};

export type BatchOutputRejectResponse = {
    status: BatchOutputMutationStatus;
    message?: string;
    rawImageId?: number;
};

export type BatchOutputRegenerateResponse = {
    status: BatchOutputMutationStatus;
    message?: string;
    rawImageId?: number;
    processedFilePath?: string;
    outputUrl?: string;
};

export type BatchOutputSendToPhotoshopResponse = {
    status: BatchOutputMutationStatus;
    message?: string;
    barcode?: string;
    transferredFilesCount?: number;
    targetS3FolderPrefix?: string;
};

const pick = (raw: Record<string, unknown>, camel: string, pascal: string) =>
    raw[camel] ?? raw[pascal];

/** BFF Newtonsoft bazen enum'u sayı (1=Completed) gönderir. */
const normalizeMutationStatus = (value: unknown): BatchOutputMutationStatus => {
    if (value == null) {
        return '';
    }
    if (typeof value === 'number') {
        switch (value) {
            case 0:
                return 'Skipped';
            case 1:
                return 'Completed';
            case 2:
                return 'Failed';
            case 3:
                return 'ValidationFailed';
            default:
                return String(value);
        }
    }
    const text = String(value).trim();
    if (text === '1') {
        return 'Completed';
    }
    if (text === '0') {
        return 'Skipped';
    }
    if (text === '2') {
        return 'Failed';
    }
    if (text === '3') {
        return 'ValidationFailed';
    }
    return text;
};

const isMutationSuccess = (status: BatchOutputMutationStatus) =>
    status === 'Completed';

const mapApprove = (raw: Record<string, unknown>): BatchOutputApproveResponse => ({
    status: normalizeMutationStatus(pick(raw, 'status', 'Status')),
    message: String(pick(raw, 'message', 'Message') ?? '') || undefined,
    rawImageId: Number(pick(raw, 'rawImageId', 'RawImageId') ?? 0) || undefined,
    barcode: String(pick(raw, 'barcode', 'Barcode') ?? '') || undefined,
    barcodeFullyApproved: Boolean(pick(raw, 'barcodeFullyApproved', 'BarcodeFullyApproved')),
    readyProcessedPaths: (
        (pick(raw, 'readyProcessedPaths', 'ReadyProcessedPaths') ?? []) as unknown[]
    ).map(String),
    fileTransferTriggered: Boolean(pick(raw, 'fileTransferTriggered', 'FileTransferTriggered')),
});

const mapReject = (raw: Record<string, unknown>): BatchOutputRejectResponse => ({
    status: normalizeMutationStatus(pick(raw, 'status', 'Status')),
    message: String(pick(raw, 'message', 'Message') ?? '') || undefined,
    rawImageId: Number(pick(raw, 'rawImageId', 'RawImageId') ?? 0) || undefined,
});

const mapRegenerate = (raw: Record<string, unknown>): BatchOutputRegenerateResponse => ({
    status: normalizeMutationStatus(pick(raw, 'status', 'Status')),
    message: String(pick(raw, 'message', 'Message') ?? '') || undefined,
    rawImageId: Number(pick(raw, 'rawImageId', 'RawImageId') ?? 0) || undefined,
    processedFilePath: String(pick(raw, 'processedFilePath', 'ProcessedFilePath') ?? '') || undefined,
    outputUrl: String(pick(raw, 'outputUrl', 'OutputUrl') ?? '') || undefined,
});

const mapSendToPhotoshop = (raw: Record<string, unknown>): BatchOutputSendToPhotoshopResponse => ({
    status: normalizeMutationStatus(pick(raw, 'status', 'Status')),
    message: String(pick(raw, 'message', 'Message') ?? '') || undefined,
    barcode: String(pick(raw, 'barcode', 'Barcode') ?? '') || undefined,
    transferredFilesCount: Number(pick(raw, 'transferredFilesCount', 'TransferredFilesCount') ?? 0),
    targetS3FolderPrefix:
        String(pick(raw, 'targetS3FolderPrefix', 'TargetS3FolderPrefix') ?? '') || undefined,
});

const Approve = async (rawImageId: number, createdBy?: string): Promise<BatchOutputApproveResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.BatchOutputCommandURL.Approve,
        method: RequestType.POST,
        data: { rawImageId, createdBy },
        skipErrorModal: true,
    })) as Record<string, unknown>;
    return mapApprove(raw);
};

const Reject = async (
    rawImageId: number,
    rejectKey: string,
    rejectNote?: string,
    createdBy?: string,
): Promise<BatchOutputRejectResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.BatchOutputCommandURL.Reject,
        method: RequestType.POST,
        data: { rawImageId, rejectKey, rejectNote, createdBy },
        skipErrorModal: true,
    })) as Record<string, unknown>;
    return mapReject(raw);
};

const Regenerate = async (payload: {
    rawImageId: number;
    sourceImageUrl: string;
    productBarcode?: string;
    prompt?: string;
    extraPrompt?: string;
    username?: string;
}): Promise<BatchOutputRegenerateResponse> => {
    const raw = (await longRunningRequest({
        url: ApiURL.Command.StudioCommandURLs.BatchOutputCommandURL.Regenerate,
        method: RequestType.POST,
        data: payload,
        skipErrorModal: true,
    })) as Record<string, unknown>;
    return mapRegenerate(raw);
};

const SendToPhotoshop = async (
    barcode: string,
    username?: string,
): Promise<BatchOutputSendToPhotoshopResponse> => {
    const environmentPrefix = String(import.meta.env.VITE_ENVIRONMENT ?? '')
        .trim()
        .replace(/\/+$/, '');
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.BatchOutputCommandURL.SendToPhotoshop,
        method: RequestType.POST,
        data: {
            barcode,
            username,
            createdBy: username,
            environmentPrefix: environmentPrefix || undefined,
        },
        skipErrorModal: true,
    })) as Record<string, unknown>;
    return mapSendToPhotoshop(raw);
};

export const BatchOutputCommand = {
    Approve,
    Reject,
    Regenerate,
    SendToPhotoshop,
    isMutationSuccess,
};
